import { Form, Formik, useField } from 'formik';
import * as yup from 'yup';
import { FlaskConical } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';
import { ExperimentalFeatures } from '@/react/portainer/settings/types';
import { useUpdateExperimentalSettingsMutation } from '@/react/portainer/settings/queries';

import { LoadingButton } from '@@/buttons/LoadingButton';
import { TextTip } from '@@/Tip/TextTip';
import { SwitchField } from '@@/form-components/SwitchField';
import { Alert } from '@@/Alert';

interface FormValues {
  fleetManagement?: boolean;
}

const validation = yup.object({
  fleetManagement: yup.boolean(),
});

interface Props {
  settings: ExperimentalFeatures;
}

export function ExperimentalFeaturesSettingsForm({ settings }: Props) {
  const initialValues: FormValues = settings;
  const updateSettingsMutation = useUpdateExperimentalSettingsMutation();

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validation}
      validateOnMount
      enableReinitialize
    >
      {({ isValid, dirty, isSubmitting }) => (
        <Form className="form-horizontal">
          <TextTip color="blue" icon={FlaskConical}>
            Experimental features may be discontinued without notice.
          </TextTip>

          <br />
          <br />

          <div className="form-group col-sm-12 text-muted small">
            In Portainer releases, we may introduce features that we&apos;re
            experimenting with. These will be items in the early phases of
            development with limited testing.
            <br />
            Our goal is to gain early user feedback, so we can refine, enhance
            and ultimately make our features the best they can be. Disabling an
            experimental feature will prevent access to it.
          </div>

          <div className="form-group col-sm-12">
            <Alert
              color="info"
              title="Fleet management is in active development and some functionality is limited:"
            >
              <ul>
                <li>
                  Policies may fail to apply if there are existing settings at
                  an environment level.
                </li>
                <li>
                  Existing environment level settings will not restore when a
                  policy is removed from an environment.
                </li>
                <li>
                  At this time we recommend using fleet management only for
                  fresh new environments.
                </li>
              </ul>
            </Alert>
          </div>

          <div className="form-group col-sm-12">
            <Alert
              color="warn"
              title="Fleet management is for Edge (Standard) Agent environments. It requires Edge Agent >=2.37.0"
            />
          </div>

          <FleetManagementToggle />

          <div className="form-group">
            <div className="col-sm-12">
              <LoadingButton
                loadingText="Saving settings..."
                isLoading={isSubmitting}
                disabled={!isValid || !dirty}
                className="!ml-0"
                data-cy="settings-experimentalButton"
              >
                Save experimental settings
              </LoadingButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );

  async function handleSubmit(values: FormValues) {
    updateSettingsMutation.mutateAsync(values, {
      onSuccess() {
        notifySuccess(
          'Success',
          'Successfully updated experimental features settings'
        );
      },
    });
  }
}

function FleetManagementToggle() {
  const [{ name, value }, , { setValue }] =
    useField<FormValues['fleetManagement']>('fleetManagement');

  return (
    <div className="form-group">
      <div className="col-sm-12">
        <SwitchField
          data-cy="experimental-fleetManagementSwitch"
          label="Fleet management"
          tooltip="Enable fleet management features including policies for environment groups."
          checked={value || false}
          onChange={(checked) => setValue(checked)}
          name={name}
          labelClass="col-sm-3 col-lg-2"
        />
      </div>
    </div>
  );
}
